import React from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import DownloadTemplate from './DownloadTemplate'
import DnaPlateUploadMethod from './DnaPlateUploadMethod'
import Button from '@material-ui/core/Button'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../messages'
import {
  useUploadCopyPasteTextMutation,
  useFileUploadPlateMutation,
  usePlateSessionFieldMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import {
  ActiveLinkTypeDnaplate,
  Status,
  FileFormat,
  PlateProduct,
  UploadType,
  DnaplateFileHeaders,
} from '../enum'
import { dnaPlatesRoute, useRouter } from '@src/routes'
import { configContext } from '../DnaPlates'
import {
  EmptyStatePlate,
  EmptyWellPlate,
  EmptyDuplexWell,
} from '../EmptySession'
import { EmptyPrice } from '../Reducer'
import clsx from 'clsx'
import { Checkbox, FormControlLabel } from '@material-ui/core'
import Switch from '@material-ui/core/Switch'
import FormGroup from '@material-ui/core/FormGroup'
import {
  validateTextUpdated as validateText,
  convertToASCII,
  checkMultipleLine,
} from '@src/routes/Configurators/utils/util'
import CautionPopUp from '@src/routes/Configurators/components/CautionPopup'
import CircleLoader from '@src/components/CircleLoader'
import { ConfiguratorValues } from '@src/routes/Configurators/utils/constants'
import globalStyles from '@src/routes/Configurators/utils/styles'

const useStyles = makeStyles((theme: Theme) => {
  return {
    contentWrapper: {
      backgroundColor: 'white',
      padding: theme.spacing(5, 5),
      flexGrow: 1,
    },
    root: {
      flexGrow: 0,
    },

    stepDescription: {
      textAlign: 'left',
      fontSize: theme.typography.pxToRem(15),
      margin: theme.spacing(1, 0, 0, 3),
    },
    step: {
      textAlign: 'left',
      fontSize: theme.typography.pxToRem(20),
      color: '#34d6e6',
    },
    stepHeight: {
      marginTop: theme.spacing(6),
      marginLeft: theme.spacing(4),
    },
    uploadButton: {
      backgroundColor: theme.palette.primary.main,
      width: theme.spacing(50),
      color: theme.palette.common.white,
      '&:disabled': {
        color: theme.palette.common.white,
      },
      marginTop: theme.spacing(-1),
      marginLeft: theme.spacing(6),
    },
    uploadButtonSection: {
      marginTop: theme.spacing(12),
      marginLeft: theme.spacing(4),
      marginBottom: theme.spacing(50),
    },
    headingTop: {
      fontSize: theme.typography.pxToRem(15),
    },
    uploadDisable: {
      backgroundColor: theme.palette.grey[500],
    },
    overridecheckBox: {
      paddingLeft: theme.spacing(20),
    },
    over1: {
      fontSize: theme.typography.pxToRem(14),
      color: '#1d5293',
      fontWeight: theme.typography.fontWeightMedium,
    },
    switchPosition: {
      padding: theme.spacing(0, 0, 4, 0),
      width: theme.spacing(83),
    },
    textSize: {
      fontSize: theme.typography.pxToRem(14),
      marginTop: theme.spacing(3),
      marginLeft: theme.spacing(-5),
    },
  }
})

interface OligosInput {
  row: string
  column: string
  name: string
  fiveMod?: string
  sequence: string
  threeMod?: string
  comments?: string
}

interface DuplexInput {
  row: string
  column: string
  name: string
  fiveMod?: string
  sequence: string
  customerOverhang?: string
  threeMod?: string
  antisenceName: string
  antisenceFiveMod?: string
  antisenceSequence: string
  customerAntisenseOverHang?: string
  antisenceThreeMod?: string
  comments?: string
}

export const Plate = {
  plateName: '',
  wellList: [],
  biTubeWellList: [],
}
const PlateName = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11']
let startRow: any = 0
export const parseSpreadsheet = async (
  file?: File,
  productType?: string
): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!file) {
      return resolve('')
    }
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const plateList: any = []
        const XLSX = await import('xlsx')
        const data = new Uint8Array((e.target as any).result)
        const workbook = XLSX.read(data, { type: 'array' })
        let products: OligosInput[] = []
        let duplexproduct: DuplexInput[] = []
        const buildDuplexProduct = (oligo) => {
          let {
            row,
            column,
            name,
            fiveMod,
            sequence,
            customerOverhang,
            threeMod,
            antisenceName,
            antisenceFiveMod,
            antisenceSequence,
            customerAntisenseOverHang,
            antisenceThreeMod,
            comments,
          } = oligo
          row = row ? row.toString() : ''
          column = column ? column.toString() : ''
          name = name ? name.toString() : ''
          fiveMod = fiveMod ? fiveMod.toString().trim() : ''
          sequence = sequence ? sequence.toString() : ''
          customerOverhang = customerOverhang ? customerOverhang.toString() : ''
          threeMod = threeMod ? threeMod.toString().trim() : ''
          antisenceName = antisenceName ? antisenceName.toString() : ''
          antisenceFiveMod = antisenceFiveMod
            ? antisenceFiveMod.toString().trim()
            : ''
          antisenceSequence = antisenceSequence
            ? antisenceSequence.toString()
            : ''
          customerAntisenseOverHang = customerAntisenseOverHang
            ? customerAntisenseOverHang.toString()
            : ''
          antisenceThreeMod = antisenceThreeMod
            ? antisenceThreeMod.toString().trim()
            : ''
          comments = comments ? comments.toString().trim() : ''

          return {
            row,
            column,
            name,
            fiveMod,
            sequence,
            customerOverhang,
            threeMod,
            antisenceName,
            antisenceFiveMod,
            antisenceSequence,
            customerAntisenseOverHang,
            antisenceThreeMod,
            comments,
          }
        }
        const buildOligosProduct = (oligo) => {
          let { row, column, name, fiveMod, sequence, threeMod, comments } =
            oligo
          row = row ? row.toString() : ''
          column = column ? column.toString() : ''
          name = name ? name.toString() : ''
          fiveMod = fiveMod ? fiveMod.toString() : ''
          sequence = validateText(
            convertToASCII(sequence ? sequence.toString() : '')
          )
          threeMod = threeMod ? threeMod.toString() : ''
          comments = comments ? comments.toString().trim() : ''

          return {
            row,
            column,
            name,
            fiveMod,
            sequence,
            threeMod,
            comments,
          }
        }
        if (productType === PlateProduct.DuplexPlate) {
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const hasHeaders = true
          const emptyPlate = Object.assign({}, Plate)
          plateList.push(emptyPlate)
          plateList[0].plateName = sheetName
          if (
            sheetName === UploadType.SiRNA_96well_8channel ||
            sheetName === UploadType.SiRNA_96well_12channel ||
            sheetName === UploadType.SiRNA_384well
          ) {
            worksheet['!ref'] = 'A17:M405'
            startRow = 18
            duplexproduct = (
              XLSX.utils.sheet_to_json(worksheet, {
                header: [
                  'row',
                  'column',
                  'name',
                  'fiveMod',
                  'sequence',
                  'customerOverhang',
                  'threeMod',
                  'antisenceName',
                  'antisenceFiveMod',
                  'antisenceSequence',
                  'customerAntisenseOverHang',
                  'antisenceThreeMod',
                  'comments',
                ],
              }) as DuplexInput[]
            ).map(buildDuplexProduct)
            if (hasHeaders) {
              duplexproduct.splice(0, 1)
              checkMultipleLine(duplexproduct, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
          plateList[0].biTubeWellList = duplexproduct
          plateList[0].wellList = []
        } else {
          PlateName.forEach((nameforSheet, index) => {
            const sheetName = workbook.SheetNames[index]
            const worksheet = workbook.Sheets[sheetName]
            const hasHeaders = true
            //ignore sheets without name and the one with ordering code
            if (
              sheetName !== undefined &&
              !(
                worksheet['A3'] !== undefined &&
                worksheet['A3'].v ===
                  DnaplateFileHeaders.ModificationDescription &&
                worksheet['B3'] !== undefined &&
                worksheet['B3'].v === DnaplateFileHeaders.Position &&
                worksheet['C3'] !== undefined &&
                worksheet['C3'].v === DnaplateFileHeaders.Code
              )
            ) {
              const emptyPlate = Object.assign({}, Plate)
              plateList.push(emptyPlate)
              plateList[index].plateName = sheetName
              if (
                worksheet['A7'] !== undefined &&
                worksheet['A7'].v === DnaplateFileHeaders.Row &&
                worksheet['B7'].v === DnaplateFileHeaders.Column &&
                worksheet['C7'].v === DnaplateFileHeaders.Name &&
                worksheet['D7'].v === DnaplateFileHeaders.Mod_5 &&
                worksheet['E7'].v === DnaplateFileHeaders.Sequence &&
                worksheet['F7'].v === DnaplateFileHeaders.Mod_3 &&
                worksheet['G7'] === undefined
              ) {
                worksheet['!ref'] = 'A7:G780'
                startRow = 8
                products = (
                  XLSX.utils.sheet_to_json(worksheet, {
                    header: [
                      'row',
                      'column',
                      'name',
                      'fiveMod',
                      'sequence',
                      'threeMod',
                      'comments',
                    ],
                    raw: false,
                  }) as OligosInput[]
                ).map((oligo) => {
                  let {
                    row,
                    column,
                    name,
                    fiveMod,
                    sequence,
                    threeMod,
                    comments,
                  } = oligo
                  row = row ? row.toString() : ''
                  column = column ? column.toString() : ''
                  name = name ? name.toString() : ''
                  fiveMod = fiveMod ? fiveMod.toString() : ''
                  sequence = validateText(
                    convertToASCII(sequence ? sequence.toString() : '')
                  )
                  threeMod = threeMod ? threeMod.toString() : ''
                  comments = comments ? comments.toString().trim() : ''

                  return {
                    row,
                    column,
                    name,
                    fiveMod,
                    sequence,
                    threeMod,
                    comments,
                  }
                })
                if (hasHeaders) {
                  products.splice(0, 1)
                  checkMultipleLine(products, resolve)
                }
              } else if (
                worksheet['A3'] !== undefined &&
                worksheet['A3'].v === DnaplateFileHeaders.Row &&
                worksheet['B3'].v === DnaplateFileHeaders.Column &&
                worksheet['C3'].v === DnaplateFileHeaders.Name &&
                worksheet['D3'].v === DnaplateFileHeaders.Mod_5 &&
                worksheet['E3'].v === DnaplateFileHeaders.Sequence &&
                worksheet['F3'].v === DnaplateFileHeaders.Mod_3 &&
                worksheet['G3'] === undefined
              ) {
                worksheet['!ref'] = 'A3:G780'
                startRow = 4
                products = (
                  XLSX.utils.sheet_to_json(worksheet, {
                    header: [
                      'row',
                      'column',
                      'name',
                      'fiveMod',
                      'sequence',
                      'threeMod',
                      'comments',
                    ],
                    raw: false,
                  }) as OligosInput[]
                ).map(buildOligosProduct)
                if (hasHeaders) {
                  products.splice(0, 1)
                  checkMultipleLine(products, resolve)
                }
              } else {
                resolve(FileFormat.InvalidTemplate)
              }
              plateList[index].wellList = products
              plateList[index].biTubeWellList = []
            }
          })
        }
        resolve(plateList)
      } catch (err) {
        reject(err)
      }
    }
    reader.readAsArrayBuffer(file)
  })

const SequenceUpload = (props) => {
  const { productDetails } = props
  const intl = useIntl()
  const [text, setText] = React.useState('')
  const [uploadCopyPasteText, { loading: copypasteLoading }] =
    useUploadCopyPasteTextMutation()
  const [platesessionField] = usePlateSessionFieldMutation()
  const [upload, setUpload] = React.useState(false)
  const { state, dispatch } = React.useContext(configContext)
  const [validationMessage, setValidationMessage] = React.useState<string[]>([])
  const [fileUploadPlate, { loading: fileUploadLoading }] =
    useFileUploadPlateMutation()
  const [disableUpload, setDisableUpload] = React.useState(true)
  const [override, setOverride] = React.useState(false)
  const [autoGenerate, setAutoGenerate] = React.useState<boolean>(false)
  let { sessionData } = state
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [uploadDataType, setUploadDataType] = React.useState('')
  const [datavalue, setDatavalue] = React.useState<any>('')

  const router = useRouter()
  const path = `${dnaPlatesRoute.index()}?product=${
    productDetails.productType
  }&activeLink=${ActiveLinkTypeDnaplate.OligoConfiguration}`

  const savePlateDataToSession = async (obj) => {
    await platesessionField({
      variables: { input: obj, productInput: productDetails },
    })
  }

  const setData = (obj) => {
    sessionData = [EmptyStatePlate]
    obj.forEach((item: any, index: number) => {
      if (index > 0) {
        const emptyObj = Object.assign({}, EmptyStatePlate)
        sessionData.push(emptyObj)
      }
      sessionData[index].isCSR = item.isCSR
      sessionData[index].isCSRSupervisor = item.isCSRSupervisor
      sessionData[index].wellType = item.wellType
      sessionData[index].plateName = item.plateName
      if (item.researcher) {
        sessionData[index].researcher = item.researcher
      } else {
        sessionData[index].researcher = ''
      }
      sessionData[index].channelType = item.channelType
      sessionData[index].plateType = item.plateType
      sessionData[index].countryCode = item.countryCode
      sessionData[index].totalBaseCount = item.totalBaseCount
      sessionData[index].plateId = item.plateId
      sessionData[index].countOfWells = item.countOfWells
      sessionData[index].material = item.material
      sessionData[index].isAmountSelected = item.isAmountSelected
      sessionData[index].isConcentrationSelected = item.isConcentrationSelected
      sessionData[index].purification = ''
      sessionData[index].format = ''
      sessionData[index].concentration = ''
      sessionData[index].amount = ''
      sessionData[index].scale = ''
      sessionData[index].daughterPlateCount = item.daughterPlateCount
      if (item.isOverridden) {
        sessionData[index].isOverridden = item.isOverridden
      } else {
        sessionData[index].isOverridden = false
      }
      sessionData[index].isSpecificationsOverridden =
        item.isSpecificationsOverridden
      sessionData[index].isShippingOverridden = item.isShippingOverridden
      sessionData[index].isPackageTypeOther = item.isPackageTypeOther
      sessionData[index].minAmount = item.minAmount
      sessionData[index].maxAmount = item.maxAmount
      sessionData[index].plateCopiesRange = item.plateCopiesRange
      sessionData[index].pageNo = item.pageNo
      sessionData[index].applyToAll = item.applyToAll
      sessionData[index].researcherNameError = []
      sessionData[index].plateNameError = []
      sessionData[index].category = ''
      sessionData[index].packageType = ''
      sessionData[index].shipping = ''
      sessionData[index].stockPlate = ''
      sessionData[index].selectedPackageType = ''
      sessionData[index].isConcentrationOther = false
      sessionData[index].productType = productDetails.productType
      if (productDetails.productType === PlateProduct.DuplexPlate) {
        sessionData[index].wellList = []
        const WellListData: any = []
        item.biTubeWellList.forEach((data: any, indexWell: number) => {
          const emptyWellObj = Object.assign({}, EmptyDuplexWell)
          WellListData.push(emptyWellObj)
          WellListData[indexWell].id = data.id
          WellListData[indexWell].name = data.name
          WellListData[indexWell].sequence = data.sequence
          WellListData[indexWell].baseCount = data.baseCount
          WellListData[indexWell].threeMod = data.threeMod
          WellListData[indexWell].fiveMod = data.fiveMod
          WellListData[indexWell].antisenceBaseCount = data.antisenceBaseCount
          WellListData[indexWell].antisenceName = data.antisenceName
          WellListData[indexWell].antisenceSequence = data.antisenceSequence
          if (
            data.autoGenerateOligo === undefined ||
            data.autoGenerateOligo === null
          ) {
            WellListData[indexWell].autoGenerateOligo = false
          } else {
            WellListData[indexWell].autoGenerateOligo = data.autoGenerateOligo
          }
          WellListData[indexWell].customerOverhang = data.customerOverhang
          WellListData[indexWell].customerAntisenseOverHang =
            data.customerAntisenseOverHang
          WellListData[indexWell].antisenceFiveMod = data.antisenceFiveMod
          WellListData[indexWell].antisenceThreeMod = data.antisenceThreeMod
          WellListData[indexWell].format = ''
          WellListData[indexWell].purification = ''
          WellListData[indexWell].concentration = ''
          WellListData[indexWell].scale = ''
          WellListData[indexWell].autoValidation = data.autoValidation
          WellListData[indexWell].countryCode = data.countryCode
          WellListData[indexWell].productType = data.productType
          WellListData[indexWell].technicalSequence = data.technicalSequence
          WellListData[indexWell].technicalAntisenceSequence =
            data.technicalAntisenceSequence
          WellListData[indexWell].amount = ''
          WellListData[indexWell].tubes = data.tubes
          WellListData[indexWell].row = data.row
          WellListData[indexWell].column = data.column
          if (data.comments === undefined || data.comments === null) {
            WellListData[indexWell].comments = ''
          } else {
            WellListData[indexWell].comments = data.comments
          }
          if (data.additionalComments) {
            WellListData[indexWell].additionalComments = data.additionalComments
          } else {
            WellListData[indexWell].additionalComments = ''
          }

          WellListData[indexWell].isOverridden = data.isOverridden
          if (data.orderItemId === undefined || data.orderItemId === null) {
            WellListData[indexWell].orderItemId = ''
          } else {
            WellListData[indexWell].orderItemId = data.orderItemId
          }
          if (
            data.senseNameCheck === undefined ||
            data.senseNameCheck === null
          ) {
            WellListData[indexWell].senseNameCheck = false
          } else {
            WellListData[indexWell].senseNameCheck = data.senseNameCheck
          }
          WellListData[indexWell].pageNo = data.pageNo
          WellListData[indexWell].packageType = ''
          WellListData[indexWell].shipping = ''
          if (data.terms === undefined || data.terms === null) {
            WellListData[indexWell].terms = false
          } else {
            WellListData[indexWell].terms = data.terms
          }
          WellListData[indexWell].molecularWeight = data.molecularWeight
          WellListData[indexWell].primerDimer = data.primerDimer
          WellListData[indexWell].secondaryStructure = data.secondaryStructure
          WellListData[indexWell].gcContent = data.gcContent
          WellListData[indexWell].meltingTemparature = data.meltingTemparature
          WellListData[indexWell].antisenseMolecularWeight =
            data.antisenseMolecularWeight
          WellListData[indexWell].antisensePrimerDimer =
            data.antisensePrimerDimer
          WellListData[indexWell].antisenseMeltingTemparature =
            data.antisenseMeltingTemparature
          WellListData[indexWell].antisenseGcContent = data.antisenseGcContent
          WellListData[indexWell].antisenseSecondaryStructure =
            data.antisenseSecondaryStructure
          WellListData[indexWell].listError = data.listError
          WellListData[indexWell].listWarning = data.listWarning
        })
        sessionData[index].biTubeWellList = WellListData
      } else {
        sessionData[index].biTubeWellList = []
        const WellListData: any = []
        item.wellList.forEach((data: any, indexWell: number) => {
          const emptyWellObj = Object.assign({}, EmptyWellPlate)
          WellListData.push(emptyWellObj)
          WellListData[indexWell].id = data.id
          if (item.plateType === 'STANDARD') {
            WellListData[indexWell].oligoId = data.oligoId
          }
          WellListData[indexWell].name = data.name
          WellListData[indexWell].wellType = data.wellType
          if (data.mixed) {
            WellListData[indexWell].mixed = data.mixed
          } else {
            WellListData[indexWell].mixed = null
          }
          WellListData[indexWell].sequence = data.sequence
          WellListData[indexWell].baseCount = data.baseCount
          WellListData[indexWell].threeMod = data.threeMod
          WellListData[indexWell].fiveMod = data.fiveMod
          WellListData[indexWell].format = ''
          WellListData[indexWell].other = ''
          WellListData[indexWell].purification = ''
          WellListData[indexWell].concentration = ''
          WellListData[indexWell].category = ''
          WellListData[indexWell].scale = ''
          WellListData[indexWell].countryCode = data.countryCode
          WellListData[indexWell].productType = data.productType
          WellListData[indexWell].technicalSequence = data.technicalSequence
          WellListData[indexWell].amount = ''
          WellListData[indexWell].tubes = data.tubes
          WellListData[indexWell].terms = data.terms
          WellListData[indexWell].flag = data.flag
          WellListData[indexWell].row = data.row
          WellListData[indexWell].column = data.column
          if (data.comments === undefined || data.comments === null) {
            WellListData[indexWell].comments = ''
          } else {
            WellListData[indexWell].comments = data.comments
          }
          if (data.additionalComments) {
            WellListData[indexWell].additionalComments = data.additionalComments
          } else {
            WellListData[indexWell].additionalComments = ''
          }
          WellListData[indexWell].isOverridden = data.isOverridden
          WellListData[indexWell].orderItemId = data.orderItemId
          WellListData[indexWell].pageNo = data.pageNo
          WellListData[indexWell].packageType = ''
          WellListData[indexWell].shipping = ''
          WellListData[indexWell].excessMaterial = false
          WellListData[indexWell].molecularWeight = data.molecularWeight
          WellListData[indexWell].primerDimer = data.primerDimer
          WellListData[indexWell].secondaryStructure = data.secondaryStructure
          WellListData[indexWell].gcContent = data.gcContent
          WellListData[indexWell].meltingTemparature = data.meltingTemparature
          WellListData[indexWell].epsilon = data.epsilon
          WellListData[indexWell].listError = data.listError
          WellListData[indexWell].listWarning = data.listWarning
        })
        sessionData[index].wellList = WellListData
      }
    })
    const emptyPriceObj = Object.assign({}, EmptyPrice)
    sessionData.forEach((data) => {
      data.pageNo = 1
      delete data.__typename
      data.wellList.forEach((well) => {
        delete well.__typename
        well.listError.forEach((error) => {
          delete error.__typename
        })
        well.listWarning.forEach((warning) => {
          delete warning.__typename
        })
      })
      data.biTubeWellList.forEach((duplexwell) => {
        delete duplexwell.__typename
        duplexwell.listError.forEach((error) => {
          delete error.__typename
        })
        duplexwell.listWarning.forEach((warning) => {
          delete warning.__typename
        })
      })
    })
    dispatch({
      type: 'UPDATE_CURRENT_PLATE_INDEX',
      data: 0,
    })
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...sessionData],
    })
    dispatch({
      type: 'UPDATE_PLATE_PRICE',
      data: [emptyPriceObj],
    })
    dispatch({ type: 'SET_STEP_NO', data: 1 })
    savePlateDataToSession(sessionData)
  }

  const ValidateCopyPaste = (key, value) => {
    setValidationMessage([])
    if (key && key !== '') {
      if (key === Status.success && value) {
        const obj: any = JSON.parse(value)
        setData(obj)
        router.push(path)
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: '',
        })
      } else {
        setValidationMessage([key])
        setDisableUpload(true)
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setText('')
        window.scrollTo(0, 0)
      }
    }
  }

  const copyPaste = async (obj) => {
    const response = await uploadCopyPasteText({
      variables: {
        input: obj,
        productInput: productDetails,
        isOverride: override,
        autogenerateAntisense: autoGenerate,
      },
    })
    const { data } = response
    if (data && data.uploadCopyPasteText) {
      if (!data.uploadCopyPasteText.serverError) {
        const { key, value } = data.uploadCopyPasteText
        ValidateCopyPaste(key, value)
      } else {
        window.scrollTo(0, 0)
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: ConfiguratorValues.InternalServerError,
        })
      }
    }
  }

  const handleText = (e) => {
    if (e.target.value.length !== 0) {
      setDisableUpload(false)
    } else {
      setDisableUpload(true)
    }
    setText(e.target.value)
    setValidationMessage([])
    dispatch({
      type: 'SET_PAGE_ERROR',
      data: '',
    })
  }

  const handleOverride = (value) => {
    setOverride(value)
    if (text.length !== 0) {
      setDisableUpload(false)
    }
    setValidationMessage([])
    dispatch({
      type: 'SET_PAGE_ERROR',
      data: '',
    })
  }

  const validateCopyPasteText = () => {
    if (text !== '' && text.length !== 0) {
      setUpload(false)
      if (productDetails.productType === PlateProduct.DuplexPlate) {
        if (
          sessionData.length === 1 &&
          sessionData[0].biTubeWellList.length === 1 &&
          sessionData[0].biTubeWellList[0].id === 0
        ) {
          copyPaste(convertToASCII(text))
        } else {
          setUploadDataType(UploadType.copyPaste)
          setDatavalue(text)
          setDialogOpen(true)
        }
      } else {
        if (
          sessionData.length === 1 &&
          sessionData[0].wellList.length === 1 &&
          sessionData[0].wellList[0].id === 0
        ) {
          copyPaste(convertToASCII(text))
        } else {
          setUploadDataType(UploadType.copyPaste)
          setDatavalue(text)
          setDialogOpen(true)
        }
      }
    }
  }

  const handleUploadChange = async (file) => {
    dispatch({ type: 'SET_PAGE_ERROR', data: '' })
    setUpload(true)
    setValidationMessage([])
    if (
      FileFormat.AcceptFormat &&
      FileFormat.AcceptFormat.split(',')
        .map((type) => type.trim().toLowerCase())
        .includes(file?.type)
    ) {
      let uploadInput: any = ''
      uploadInput = await parseSpreadsheet(file, productDetails.productType)
      if (uploadInput === FileFormat.InvalidTemplate) {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([
          intl.formatMessage(messages.CONF_INVALID_TEMPLATE),
        ])
      }
      if (uploadInput === FileFormat.MultipleLineError) {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([intl.formatMessage(messages.MULTIPLE_LINE_ERROR)])
      }
      if (uploadInput === '') {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([intl.formatMessage(messages.CONF_EMPTY_FILE)])
      }
      if (
        uploadInput !== '' &&
        uploadInput !== FileFormat.MultipleLineError &&
        uploadInput !== FileFormat.InvalidTemplate
      ) {
        //make api call here passing inputStr as input
        const response = await fileUploadPlate({
          fetchPolicy: 'no-cache',
          variables: {
            input: uploadInput,
            productInput: productDetails,
            isOverride: override,
            autogenerateAntisense: autoGenerate,
            startRow: startRow,
          },
        })
        const { data } = response

        if (data && data.fileUploadPlate) {
          if (!data.fileUploadPlate.serverError) {
            const { key, value } = data.fileUploadPlate
            ValidateCopyPaste(key, value)
          } else {
            window.scrollTo(0, 0)
            dispatch({
              type: 'SET_PAGE_ERROR',
              data: ConfiguratorValues.InternalServerError,
            })
          }
        }
      }
    } else {
      if (file !== undefined) {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([intl.formatMessage(messages.CONF_INVALID_FORMAT)])
      }
    }
  }

  const saveToSession = (type, data) => {
    if (type === UploadType.copyPaste) {
      copyPaste(convertToASCII(data))
    } else if (type === UploadType.fileUpload) {
      handleUploadChange(data)
    }
  }

  const validateUploadFile = (file) => {
    setText('')
    setDisableUpload(true)
    if (productDetails.productType === PlateProduct.DuplexPlate) {
      if (
        sessionData.length === 1 &&
        sessionData[0].biTubeWellList.length === 1 &&
        sessionData[0].biTubeWellList[0].id === 0
      ) {
        handleUploadChange(file)
      } else {
        setUploadDataType(UploadType.fileUpload)
        setDatavalue(file)
        setDialogOpen(true)
      }
    } else {
      if (
        sessionData.length === 1 &&
        sessionData[0].wellList.length === 1 &&
        sessionData[0].wellList[0].id === 0
      ) {
        handleUploadChange(file)
      } else {
        setUploadDataType(UploadType.fileUpload)
        setDatavalue(file)
        setDialogOpen(true)
      }
    }
  }

  const handleAutoGenerate = (value) => {
    setAutoGenerate(value)
    if (text.length !== 0) {
      setDisableUpload(false)
    }
  }
  React.useEffect(() => {
    window.scrollTo(0, 0)
    dispatch({
      type: 'SET_PAGE_ERROR',
      data: '',
    })
  }, [])

  const classes = useStyles()
  const globalClasses = globalStyles()
  return (
    <div>
      <div className={classes.contentWrapper}>
        {productDetails.productType === PlateProduct.DuplexPlate ? (
          <Grid container>
            <Grid item xs={1}>
              <FormGroup className={classes.switchPosition}>
                <Switch
                  value={autoGenerate}
                  checked={autoGenerate}
                  onChange={() => handleAutoGenerate(!autoGenerate)}
                  classes={{
                    root: globalClasses.switchRoot,
                    track: globalClasses.switchTrack,
                    switchBase: globalClasses.switchBase,
                    checked: globalClasses.checked,
                    thumb: globalClasses.thumb,
                    input: globalClasses.switchInput,
                  }}
                />
              </FormGroup>
            </Grid>
            <Grid item xs={11} className={classes.textSize}>
              <FormattedMessage
                {...messages.CONF_DUPLEX_PLATE_SWITCH_MESSAGE}
              />
            </Grid>
          </Grid>
        ) : (
          ''
        )}
        <Grid item xs={12} sm={12} className={classes.headingTop}>
          <FormattedMessage
            {...messages.CONF_PLATE_SEQUENCEUPLOAD_HEADER_TEXT}
          />
          {productDetails.isCSRSupervisor === true ? (
            <FormControlLabel
              classes={{ label: classes.over1 }}
              control={
                <Checkbox
                  value={override}
                  checked={override}
                  color="primary"
                  onChange={() => {
                    handleOverride(!override)
                  }}
                />
              }
              label={messages.CONF_OVERRIDE_VALIDATION.defaultMessage}
              className={classes.overridecheckBox}
            />
          ) : (
            ''
          )}
        </Grid>

        <Grid container spacing={3} className={classes.stepHeight}>
          <span className={classes.step}>
            <FormattedMessage {...messages.CONF_DNAPLATES_STEP1} />
          </span>
          <span className={classes.stepDescription}>
            <FormattedMessage {...messages.CONF_DNAPLATES_DOWNLOAD} />
          </span>
        </Grid>
        <DownloadTemplate productDetails={productDetails} />

        <Grid container spacing={3} className={classes.stepHeight}>
          <span className={classes.step}>
            <FormattedMessage {...messages.CONF_DNAPLATES_STEP2} />
          </span>
          <span className={classes.stepDescription}>
            <FormattedMessage {...messages.CONF_MULTIPLE_ENTRY_STEP_2} />
          </span>
        </Grid>

        <Grid container spacing={3} className={classes.stepHeight}>
          <span className={classes.step}>
            <FormattedMessage {...messages.CONF_DNAPLATES_STEP3} />
          </span>
          <span className={classes.stepDescription}>
            <FormattedMessage {...messages.CONF_MULTIPLE_ENTRY_STEP_3} />
          </span>
        </Grid>
        <DnaPlateUploadMethod
          handlechange={handleText}
          text={text}
          upload={upload}
          validationMessage={validationMessage}
          accept={FileFormat.AcceptFormat}
          productType={productDetails.productType}
          handleUploadChange={([file]) => validateUploadFile(file)}
        />
        <Grid container spacing={3} className={classes.uploadButtonSection}>
          <span className={classes.step}>
            <FormattedMessage {...messages.CONF_DNAPLATES_STEP4} />
          </span>
          <Button
            variant="contained"
            color="primary"
            size="large"
            className={clsx(
              classes.uploadButton,
              (fileUploadLoading || disableUpload) && classes.uploadDisable
            )}
            type="submit"
            onClick={validateCopyPasteText}
            disabled={fileUploadLoading || disableUpload}
            id="plates-upload-button"
            data-testid="plates-upload-button"
          >
            <FormattedMessage {...messages.CONF_UPLOAD_YOUR_DATA} />
          </Button>
        </Grid>
      </div>
      <CautionPopUp
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        saveToSession={saveToSession}
        uploadDataType={uploadDataType}
        data={datavalue}
        prodType={productDetails.productType}
      />
      {(fileUploadLoading || copypasteLoading) && <CircleLoader />}
    </div>
  )
}

export default SequenceUpload
